import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { auth } from './firebaseConfig';
import { scheduleApolloCachePersist } from './apolloCachePersistence';
import cache from './apolloCache';
import { resolveGraphQLUris } from './apiConfig';
import logger, { createRequestId } from '../utils/logger';

const { httpUri: graphQLUri, wsUri } = resolveGraphQLUris(
    process.env.REACT_APP_GRAPHQL_URI
);
const requestTimeoutMs = 15000;
const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

const fetchWithTimeout = (uri, options = {}) => {
    const controller = new AbortController();
    const {
        signal: upstreamSignal,
        timeoutMs = requestTimeoutMs,
        ...fetchOptions
    } = options;
    const abortFromUpstream = () => controller.abort(upstreamSignal?.reason);
    if (upstreamSignal?.aborted) abortFromUpstream();
    upstreamSignal?.addEventListener('abort', abortFromUpstream, { once: true });

    const timeout = setTimeout(() => controller.abort(
        new DOMException(
            'The request timed out. Refresh inventory before submitting again.',
            'TimeoutError'
        )
    ), timeoutMs);
    return fetch(uri, { ...fetchOptions, signal: controller.signal })
        .finally(() => {
            clearTimeout(timeout);
            upstreamSignal?.removeEventListener('abort', abortFromUpstream);
        });
};

const httpLink = createHttpLink({
    uri: graphQLUri,
    fetch: fetchWithTimeout,
    credentials: 'omit',
});

const wsLink = new GraphQLWsLink(createClient({
    url: wsUri,
    lazy: true,
    keepAlive: 15000,
    connectionAckWaitTimeout: 10000,
    retryAttempts: Infinity,
    retryWait: async retries => {
        const delay = Math.min(1000 * (2 ** retries), 30000);
        logger.warn('graphql.websocket_retry_scheduled', { retryAttempt: retries, delayMs: delay });
        await new Promise(resolve => setTimeout(resolve, delay + Math.random() * 500));
    },
    on: {
        connected: () => logger.info('graphql.websocket_connected'),
        closed: event => logger.warn('graphql.websocket_closed', { code: event?.code, reason: event?.reason }),
        error: error => logger.error('graphql.websocket_error', { error }),
    },
    connectionParams: async () => {
        const user = auth.currentUser;
        const idToken = user ? await user.getIdToken() : null;
        return {
            Authorization: idToken ? `Bearer ${idToken}` : '',
        };
    },
}));

const authLink = setContext(async (_, { headers }) => {
    const user = auth.currentUser;
    const idToken = user ? await user.getIdToken() : null;

    return {
        headers: {
            ...headers,
            Authorization: idToken ? `Bearer ${idToken}` : '',
        },
    };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const requestId = operation.getContext().requestId;
    graphQLErrors?.forEach(error => {
        logger.warn('graphql.operation_error', {
            requestId,
            operationName: operation.operationName || 'anonymous',
            code: error.extensions?.code,
            message: error.message,
            path: error.path,
        });
    });
    if (networkError && isOnline()) {
        logger.error('graphql.network_error', {
            requestId,
            operationName: operation.operationName || 'anonymous',
            error: networkError,
            online: true,
        });
    }
});

const diagnosticsLink = new ApolloLink((operation, forward) => {
    const requestId = createRequestId();
    const startedAt = Date.now();
    const definition = getMainDefinition(operation.query);
    const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'unknown';
    operation.setContext(({ headers = {} }) => ({
        requestId,
        headers: { ...headers, 'x-request-id': requestId },
    }));
    logger.debug('graphql.operation_started', {
        requestId,
        operationName: operation.operationName || 'anonymous',
        operationType,
    });

    return forward(operation).map(result => {
        logger.info('graphql.operation_completed', {
            requestId,
            operationName: operation.operationName || 'anonymous',
            operationType,
            durationMs: Date.now() - startedAt,
            errorCount: result.errors?.length || 0,
        });
        return result;
    });
});

const retryLink = new RetryLink({
    delay: {
        initial: 300,
        max: 3000,
        jitter: true,
    },
    attempts: {
        max: 3,
        retryIf: (error, operation) => {
            const definition = getMainDefinition(operation.query);
            const statusCode = error?.statusCode || error?.status;
            return (
                definition.operation === 'query' &&
                isOnline() &&
                !(statusCode >= 400 && statusCode < 500)
            );
        },
    },
});

const persistenceLink = new ApolloLink((operation, forward) => (
    forward(operation).map(result => {
        scheduleApolloCachePersist(cache);
        return result;
    })
));

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    wsLink,
    authLink.concat(httpLink)
);

const client = new ApolloClient({
    link: ApolloLink.from([diagnosticsLink, errorLink, retryLink, persistenceLink, splitLink]),
    cache,
    connectToDevTools: process.env.NODE_ENV !== 'production',
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        },
        query: {
            fetchPolicy: 'cache-first',
            errorPolicy: 'all',
        },
    },
});

export default client;
